import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';
import defaultTheme from 'theme/defaultTheme';
import { Orientation } from '../ExportPdfContext';
export const styles = StyleSheet.create({
    p: {
        marginBottom: 2,
    },
    container: {
        backgroundColor: defaultTheme.colors.fillMedium,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        alignContent: 'stretch',
        marginBottom: 20,
    },
    left: {
        width: 170,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
    },
    right: {
        display: 'flex',
        width: '100%',
    },
    productImageWrapper: {
        height: 170,
        padding: 8,
        display: 'flex',
    },
    productImage: {
        alignSelf: 'center',
        marginBottom: 4,
        flexGrow: 1,
        objectFit: 'contain',
    },
    productImageText: {
        paddingHorizontal: 4,
        color: '#000',
        textTransform: 'uppercase',
        fontSize: '8px',
        alignSelf: 'center',
        paddingBottom: 10,
    },
    titleWrapper: {
        marginHorizontal: 8,
        paddingTop: 8,
        paddingBottom: 8,
        marginBottom: 8,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottom: '#9C9C9C',
        borderBottomWidth: 1,
    },
    titleProductName: {
        fontFamily: 'PDF Font Bold',
        fontSize: 16,
        lineHeight: 1.3,
    },
    titleProductNumber: {
        fontSize: 8,
        flexGrow: 1,
        fontColor: '#757575',
        marginBottom: 10,
    },
    titleProductBrand: {
        fontSize: 10,
        fontFamily: 'PDF Font Bold',
        textTransform: 'uppercase',
    },
    productCertifications: {
        alignSelf: 'flex-start',
        fontFamily: 'PDF Font',
        fontSize: '8px',
        padding: '8px',
        width: '300px',
    },
    productDescription: {
        paddingHorizontal: 8,
        marginBottom: 8,
        alignSelf: 'flex-start',
        fontFamily: 'PDF Font',
        fontSize: '8px',
    },
    pricesWrapper: {
        marginTop: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    price: {
        alignSelf: 'flex-end',
        fontFamily: 'PDF Font',
        fontSize: '8px',
        padding: '2px',
    },
    priceStrong: {
        alignSelf: 'flex-end',
        fontFamily: 'PDF Font Bold',
        fontSize: '8px',
        padding: '2px',
    },
    certificationImage: {
        width: 25,
        height: 'auto',
    },
    details: {
        marginBottom: 8,
    },
    detailWrapper: {
        paddingHorizontal: 8,
        marginBottom: 2,
        display: 'flex',
        flexDirection: 'row',
    },
    productDetailHeader: {
        alignSelf: 'flex-start',
        fontFamily: 'PDF Font Bold',
        fontSize: '8px',
        width: 62,
    },
    productDetailInfo: {
        alignSelf: 'flex-start',
        fontFamily: 'PDF Font',
        fontSize: '8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
});

type Details = {
    heading: string;
    info: string;
};

type ProductCardMinimalProps = {
    image?: string | null;
    productBrandName?: string | null;
    productNumber: string;
    productName: string;
    description?: string;
    price?: string | null;
    details?: Details[];
    additionalInfo?: React.ReactNode;
    orientation?: Orientation;
};

const ProductCardMinimal = ({
    image,
    productBrandName,
    productNumber,
    productName,
    price,
    description,
    details,
    additionalInfo,
    orientation,
}: ProductCardMinimalProps) => {
    const paragraphs = description?.replaceAll('\r', '').split('\n');

    return (
        <View
            wrap={false}
            style={[
                styles.container,
                { maxWidth: orientation === 'landscape' ? 400 : 'auto', marginBottom: 4, marginRight: 4 },
            ]}
        >
            <View style={styles.left}>
                {image ? (
                    <View style={styles.productImageWrapper}>
                        <Image style={styles.productImage} src={image} />
                    </View>
                ) : null}
            </View>
            <View style={styles.right}>
                <View style={styles.titleWrapper}>
                    <View>
                        {productBrandName && <Text style={styles.titleProductBrand}>{productBrandName}</Text>}
                        <Text style={styles.titleProductNumber}>{productNumber}</Text>
                        <Text
                            style={[
                                styles.titleProductName,
                                price?.length ? { width: orientation === 'landscape' ? 215 : 240 } : { maxWidth: 400 },
                            ]}
                        >
                            {productName}
                        </Text>
                    </View>
                    <View style={styles.pricesWrapper}>
                        {price ? <Text style={styles.priceStrong}>{price}</Text> : null}
                    </View>
                </View>
                {paragraphs?.length
                    ? paragraphs.map((p, i) => (
                          <Text
                              key={i}
                              style={[
                                  styles.productDescription,
                                  {
                                      width: orientation === 'landscape' ? 250 : 325,
                                      flexGrow: 2,
                                  },
                                  styles.p,
                              ]}
                          >
                              {p}
                          </Text>
                      ))
                    : null}
                {(details?.length ?? 0) > 0 && (
                    <View style={styles.details}>
                        {details?.map((detail, index) =>
                            detail.heading && detail.info ? (
                                <View style={styles.detailWrapper} key={index}>
                                    <Text style={styles.productDetailHeader}>{detail.heading}</Text>
                                    <Text
                                        style={[
                                            styles.productDetailInfo,
                                            { maxWidth: orientation === 'landscape' ? 200 : 300 },
                                        ]}
                                    >
                                        {detail.info}
                                    </Text>
                                </View>
                            ) : null,
                        )}
                    </View>
                )}
                {additionalInfo}
            </View>
        </View>
    );
};

export default ProductCardMinimal;
